import Constants from 'js/Constants';
import { addProcess, removeProcess } from 'js/actions';
import * as actionTypes from 'js/constants/actionTypes';
import { CREATE_PROJECT, DELETE_PROJECT } from 'js/constants/processTypes';
import { DELETE, GET, POST, PUT } from 'js/constants/requestMethods';
import { RESOURCE_SUGGESTIONS_COUNT, SearchFilterViews } from 'js/constants/visibilityFilters';
import { processSearchFilters } from 'js/lib/SearchFilterUtils';
import { CALL_PRIVATE_API } from 'js/middleware/api';
import { docSchema, projectSchema } from 'js/schemas';
import { isEmpty } from 'lodash';
import { showAlert } from 'new/stores/showAlert/action';
import { loadFlowsWithoutProjectsCount } from 'js/actions/pipelines/flowsByProject';

export const getProjects =
  (page, sortCriteria, availableFilters, options = {}) =>
  (dispatch, getState) => {
    const {
      app: {
        accessControl,
        visibilityFilters: { filters },
      },
    } = getState();

    const aclQueryMode = options?.aclQueryMode ? options.aclQueryMode : accessControl.mode;

    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];
    const { processedFilters } = processSearchFilters(
      filters[SearchFilterViews.PROJECTS],
      availableFilters,
    );
    let requestMethod;
    let endpoint;
    let payload;

    if (isEmpty(processedFilters)) {
      requestMethod = GET;
      endpoint = '/projects';
      payload = {
        page,
        per_page: Constants.LISTING_ITEMS_PER_PAGE,
        sort_by: sortKey,
        sort_order: sortOrder,
      };
    } else {
      requestMethod = POST;
      endpoint = `/projects/search?page=${page}&per_page=${Constants.LISTING_ITEMS_PER_PAGE}&sort_by=${sortKey}&sort_order=${sortOrder}`;
      payload = { filters: processedFilters };
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_PROJECTS_REQUEST,
          actionTypes.GET_PROJECTS_SUCCESS,
          actionTypes.GET_PROJECTS_FAILURE,
        ],
        endpoint,
        schema: [projectSchema],
        requestMethod,
        aclQueryMode,
        payload,
        requestInfo: { view: SearchFilterViews.PROJECTS },
      },
    }).then((response) => {
      return response;
    });
  };

export const getProjectById =
  (id, options = {}) =>
  (dispatch, getState) => {
    const {
      app: { accessControl },
    } = getState();

    const aclQueryMode = options?.aclQueryMode ? options.aclQueryMode : accessControl.mode;

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_PROJECT_REQUEST,
          actionTypes.GET_PROJECT_SUCCESS,
          actionTypes.GET_PROJECT_FAILURE,
        ],
        endpoint: `/projects/${id}`,
        schema: projectSchema,
        requestMethod: GET,
        aclQueryMode,
        requestInfo: { id },
      },
    });
  };

export const getProjectsSearchSuggestions = (filters, availableFilters) => (dispatch, getState) => {
  const { app } = getState();
  const aclQueryMode = app.accessControl.mode;

  const { processedFilters } = processSearchFilters(filters, availableFilters);

  if (isEmpty(processedFilters)) {
    return;
  }

  const params = new URLSearchParams({
    page: 1,
    per_page: RESOURCE_SUGGESTIONS_COUNT,
  });

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.SEARCH_PROJECTS_REQUEST,
        actionTypes.SEARCH_PROJECTS_SUCCESS,
        actionTypes.SEARCH_PROJECTS_FAILURE,
      ],
      endpoint: `/projects/search?${params}`,
      schema: [projectSchema],
      requestMethod: POST,
      aclQueryMode,
      payload: { filters: processedFilters },
      requestInfo: { view: SearchFilterViews.PROJECTS },
    },
  });
};

export const createProjectAssignFlow =
  (flowNodeId, newInfo = {}) =>
  (dispatch) => {
    dispatch(addProcess(CREATE_PROJECT, 'Creating new project', { flowNodeId }));

    const flows = Array.isArray(flowNodeId) ? flowNodeId : flowNodeId ? [flowNodeId] : [];

    newInfo = {
      ...newInfo,
      ...(flows.length > 0 ? { flows } : {}),
    };

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.CREATE_PROJECT_ASSIGN_FLOW_REQUEST,
          actionTypes.CREATE_PROJECT_ASSIGN_FLOW_SUCCESS,
          actionTypes.CREATE_PROJECT_ASSIGN_FLOW_FAILURE,
        ],
        endpoint: `/projects`,
        schema: projectSchema,
        requestMethod: POST,
        payload: newInfo,
      },
    })
      .then(() => {
        dispatch(
          showAlert({
            title: 'Project created successfully!',
            severity: 'success',
          }),
        );
      })
      .finally(() => {
        dispatch(removeProcess(CREATE_PROJECT, { flowNodeId }));
      });
  };

/**
 * @desc Assign actioned flow to selected project
 * @param Object data | {id, type, project_id}
 */
export const assignFlowToProject = (data) => (dispatch) => {
  const requestBody = { flows: [data.id] };

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ASSIGN_FLOW_TO_PROJECT_REQUEST,
        actionTypes.ASSIGN_FLOW_TO_PROJECT_SUCCESS,
        actionTypes.ASSIGN_FLOW_TO_PROJECT_FAILURE,
      ],
      endpoint: `/projects/${data.projectId}/flows`,
      schema: null,
      requestMethod: PUT,
      payload: requestBody,
      requestInfo: data,
    },
  });
};

/**
 * @desc Assign multiple flows to selected project
 * @param Object data | {originNodeIds: number[], projectId: number}
 */
export const assignFlowsToProject = (data) => (dispatch) => {
  const isInvalidData =
    !data ||
    !data.originNodeIds ||
    !Array.isArray(data.originNodeIds) ||
    data.originNodeIds.length === 0;

  if (isInvalidData) {
    return Promise.reject(new Error('assignFlowsToProject: Invalid parameters'));
  }

  const requestBody = {
    origin_node_ids: data.originNodeIds,
    project_id: data.projectId,
  };

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ASSIGN_FLOWS_TO_PROJECT_REQUEST,
        actionTypes.ASSIGN_FLOWS_TO_PROJECT_SUCCESS,
        actionTypes.ASSIGN_FLOWS_TO_PROJECT_FAILURE,
      ],
      endpoint: '/flows/project',
      schema: null,
      requestMethod: PUT,
      payload: requestBody,
      requestInfo: data,
    },
  });
};

/**
 * @desc Remove actioned flow from selected project
 * @param Object data | {id, project_id}
 */
export const removeFlowFromProject = (data) => (dispatch) => {
  const requestBody = { flows: [data.id] };

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.REMOVE_FLOW_FROM_PROJECT_REQUEST,
        actionTypes.REMOVE_FLOW_FROM_PROJECT_SUCCESS,
        actionTypes.REMOVE_FLOW_FROM_PROJECT_FAILURE,
      ],
      endpoint: `/projects/${data.projectId}/flows`,
      schema: null,
      requestMethod: DELETE,
      payload: requestBody,
      requestInfo: data,
    },
  })
    .then(() => {
      dispatch(
        showAlert({
          title: 'Flow removed from project successfully!',
          severity: 'success',
        }),
      );
    })
    .catch((error) => {
      const errorMessage =
        error?.message ||
        error?.response?.message ||
        'Failed to remove flow from project. Please try again.';

      dispatch(
        showAlert({
          title: errorMessage,
          severity: 'error',
        }),
      );
    });
};

/**
 * @param {Object} param {id, type}
 * @returns promise
 */
export const setSelectedFlow = (data) => (dispatch) => {
  return dispatch({
    type: actionTypes.SET_SELECTED_PROJECT_FLOW,
    data,
  });
};

export const clearSelectedFlow = () => (dispatch) => {
  return dispatch({
    type: actionTypes.CLEAR_SELECTED_PROJECT_FLOW,
  });
};

/**
 * @param {Object} param {id, Object: {name | description}}
 * @returns promise
 */
export const updateProjectInfo = (id, requestBody) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_PROJECT_METAINFO_REQUEST,
        actionTypes.UPDATE_PROJECT_METAINFO_SUCCESS,
        actionTypes.UPDATE_PROJECT_METAINFO_FAILURE,
      ],
      endpoint: `/projects/${id}`,
      schema: projectSchema,
      requestMethod: PUT,
      payload: requestBody,
    },
  });
};

/**
 * @params string id as projectId
 * @desc return current project documentation
 * @returns promise
 */
export const getProjectDoc = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_PROJECT_DOCUMENTATION_REQUEST,
        actionTypes.GET_PROJECT_DOCUMENTATION_SUCCESS,
        actionTypes.GET_PROJECT_DOCUMENTATION_FAILURE,
      ],
      endpoint: `/projects/${id}/docs?expand=1`,
      schema: [docSchema],
      requestMethod: GET,
      requestInfo: {
        id: id,
      },
    },
  });
};

/**
 * @param {Object} param {name, docObject}
 * @returns promise
 */
export const updateProjectDoc = (id, content) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_PROJECT_DOCUMENTATION_REQUEST,
        actionTypes.UPDATE_PROJECT_DOCUMENTATION_SUCCESS,
        actionTypes.UPDATE_PROJECT_DOCUMENTATION_FAILURE,
      ],
      endpoint: `/projects/${id}/docs?expand=1`,
      schema: [docSchema],
      requestMethod: POST,
      payload: {
        docs: [
          {
            name: `Doc for Project ${id}`,
            description: `Doc for Project ${id}`,
            doc_type: 'md',
            text: content,
          },
        ],
      },
      requestInfo: { id },
    },
  });
};

export const deleteProject = (id) => async (dispatch) => {
  await dispatch(addProcess(DELETE_PROJECT, 'Deleting the project', { id }));

  try {
    return await dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.DELETE_PROJECTS_REQUEST,
          actionTypes.DELETE_PROJECTS_SUCCESS,
          actionTypes.DELETE_PROJECTS_FAILURE,
        ],
        endpoint: `/projects/${id}`,
        requestMethod: DELETE,
        requestInfo: { id },
      },
    }).then(() => {
      dispatch(loadFlowsWithoutProjectsCount());
    });
  } finally {
    await dispatch(removeProcess(DELETE_PROJECT, { id }));
  }
};
